var _excluded = ["input", "meta", "className"];
function _typeof(obj) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) { return typeof obj; } : function (obj) { return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }, _typeof(obj); }
function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }
function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { blurDirty, fieldPropTypesWithValue, omitLabelProps } from '../helpers';
import { LabeledField } from '../labels';
import { compose } from '../../utils';
import { parseISO } from 'date-fns';

/**
 *
 * An input component that wraps a `DatePicker` component from the [react-datepicker](https://github.com/Hacker0x01/react-datepicker) library.
 * This wrapper adds the following functionality to `DatePicker`:
 * - Adapts it to receive `redux-form`-style input props.
 * - Adds name and error labels.
 *
 * With the exception of the `input` and `meta` props, all props are passed down to the `DatePicker` component.
 * A full list of props supported by this component can be found [here](https://github.com/Hacker0x01/react-datepicker/blob/master/docs/datepicker.md). Note that unfortunately `aria-*` props are **not** supported.
 *
 * _Note: this component requires special styles in order to render correctly. To include these styles in your project, follow the directions in the main [README](README.md#dateinput-styles) file._
 *
 * @name DateInput
 * @type Function
 * @param {Object} input - A `redux-form` [input](http://redux-form.com/6.5.0/docs/api/Field.md/#input-props) object
 * @param {Object} meta - A `redux-form` [meta](http://redux-form.com/6.5.0/docs/api/Field.md/#meta-props) object
 * @example
 *
 * function BirthdayForm ({ handleSubmit }) {
 *   return (
 *     <form onSubmit={ handleSubmit }>
 *       <Field
 *          name="birthday"
 *          component={DateInput}
 *          placeholderText="mm/dd/yyyy"
 *        />
 *     </form>
 *   )
 * }
 *
 * // Will render datepicker with label "Birthday" and placeholder "mm/dd/yyyy"
 *
 */

var propTypes = _objectSpread({}, fieldPropTypesWithValue(PropTypes.oneOfType([PropTypes.object, PropTypes.string])));
var defaultProps = {
  placeholderText: 'MM/DD/YY'
};

/* Ignore test coverage: */
/* istanbul ignore next */

// React datepicker expects a date value
function parseDate(value) {
  if (!value || value instanceof Date) return value;
  return parseISO(value);
}
function DateInput(props) {
  var _omitLabelProps = omitLabelProps(props),
    _omitLabelProps$input = _omitLabelProps.input,
    name = _omitLabelProps$input.name,
    value = _omitLabelProps$input.value,
    _onBlur = _omitLabelProps$input.onBlur,
    _onChange = _omitLabelProps$input.onChange,
    meta = _omitLabelProps.meta,
    className = _omitLabelProps.className,
    rest = _objectWithoutProperties(_omitLabelProps, _excluded);
  var dateValue = parseDate(value);
  var calendarRef = useRef();
  return /*#__PURE__*/React.createElement(LabeledField, props, /*#__PURE__*/React.createElement("div", {
    className: "date-input-wrapper"
  }, /*#__PURE__*/React.createElement(DatePicker, _objectSpread({
    id: name,
    name: name,
    ref: calendarRef,
    selected: dateValue || null,
    // passing in an empty string will not default the tabbable element to today
    onBlur: function onBlur() {
      return _onBlur(value);
    },
    onChange: function onChange(val) {
      return _onChange(val !== null && val !== void 0 ? val : '');
    },
    onSelect: function onSelect() {
      /* After a user selects a date and then clicks on another element, 
         we expect onBlur to fire. However, that is _not_ happening
         because focus is not reset to the input. We're hooking into this
         lifecycle method to force the input to receive focus, which
         will then fire a blur event when it loses focus (and thus trigger onBlur).
          This is a temporary workaround until https://github.com/Hacker0x01/react-datepicker/issues/2028 is resolved.
      */
      setTimeout(function () {
        return calendarRef.current.setFocus();
      }, 0); // deferFocusInput accomplishes this, but it's likely eliminated during tree shaking since it's never used internally
    }
  }, rest))));
}
DateInput.defaultProps = defaultProps;
DateInput.propTypes = propTypes;
export default compose(blurDirty())(DateInput);